/// <amd-module name="Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions" />
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";
import { ISuiteTableItem } from "Test/TestPlansHubRefresh/TestHub/Component/TestSuiteTreeItemProvider.Types";
import * as Contracts from "TestPlanCommon/Contracts/TestPlan";
import { IListSelection } from "VSSUI/List";

export const SuitesActions = {
    /**
     * loadInitialTestData: Dispatched as InitialAction when SuitesModule is loaded.
     * Initiates data load from JSON island.
     */
    loadInitialTestData: () => createAction(SuitesActionsTypes.loadInitialTestData),

    /**
     * getMineAllPageUrl: fetch url for navigating to mine/all page
     */
    getMineAllPageUrl: () => createAction(SuitesActionsTypes.getMineAllPageUrl, {}),

    /**
     * loadInitialTestDataSucceeded: Dispatched after initial data loading completes.
     */
    loadInitialTestDataSucceeded: (initialData: Contracts.TestPlansHubRefreshData) =>
        createAction(SuitesActionsTypes.loadInitialTestDataSucceeded, initialData),

    /**
     * changeSelectedSuite: Dispatched when user changes the suite selection in the suite tree.
     */
    changeSelectedSuite: (planId: number, selectedSuiteId: number, selectedSuiteName: string) =>
        createAction(SuitesActionsTypes.changeSelectedSuite, { planId, selectedSuiteId, selectedSuiteName }),

    /**
     * openTestSuiteWitForm: Dispatched from open test suite option in suite context menu.
     */
    openTestSuiteWitForm: (testPlanId: number, testSuiteId: number, testSuiteTitle: string, rootSuiteId: number) =>
        createAction(SuitesActionsTypes.openTestSuiteWitForm, { testPlanId, testSuiteId, testSuiteTitle, rootSuiteId }),

    /**
     * updateTestSuiteName: Update selected suite title
     */
    updateTestSuiteName: (testSuiteId: number, testSuiteTitle: string, rootSuiteId: number) =>
        createAction(SuitesActionsTypes.updateTestSuiteName, { testSuiteId, testSuiteTitle, rootSuiteId }),

    /**
     * loadTestPlan: dispatched to update test plan when user changes Test Plan
     */
    loadTestPlan: (testPlan: Contracts.TestPlan) => createAction(SuitesActionsTypes.loadTestPlan, { testPlan }),

    /**
     * loadTestSuites: dispatched to update test suites when user changes Test Plan
     */
    loadTestSuites: (testSuites: Contracts.TestSuite[]) => createAction(SuitesActionsTypes.loadTestSuites, { testSuites }),

    /**
     * updateLoadingStatus: dispatched to update spinner loading status
     */
    updateLoadingStatus: (loadingStatus: boolean) => createAction(SuitesActionsTypes.updateLoadingStatus, { loadingStatus }),

    /**
     * updateReloadState: dispatched to update reload state. It is required where we need to reload the plan. After reloading, we also need to reset the state.
     */
    updateReloadState: (state: boolean) => createAction(SuitesActionsTypes.updateReloadState, { state }),

    /**
     * loadNewSuites: Dispatched after creation of test suite
     */
    loadNewSuites: (testSuites: Contracts.TestSuite[], parentSuiteId: number, rootSuiteId: number, editableSuiteId: number) =>
        createAction(SuitesActionsTypes.loadNewSuites, { testSuites, parentSuiteId, rootSuiteId, editableSuiteId }),

    /**
     * removeSuite: Dispatched after deletion of test suite. Removes a suite from the list.
     */
    removeSuite: (suiteId: number, rootSuiteId: number) => createAction(SuitesActionsTypes.removeSuite, { suiteId, rootSuiteId }),

    /**
     * createNewReqSuite: Dispatched from new suite -> requirement based suite from suite context menu
     */
    createNewReqSuite: (
        planId: number,
        parentSuiteId: number,
        parentSuiteTitle: string,
        suiteType: number,
        rootSuiteId: number,
        requirementIds: number[],
        selection: IListSelection
    ) =>
        createAction(SuitesActionsTypes.createNewReqSuite, {
            planId,
            parentSuiteId,
            parentSuiteTitle,
            suiteType,
            rootSuiteId,
            requirementIds,
            selection
        }),

    /**
     * createNewQuerySuite: Dispatched when new query based suite needs to be created.
     */
    createNewQuerySuite: (
        testSuite: Contracts.TestSuite,
        planId: number,
        parentSuiteId: number,
        parentSuiteTitle: string,
        suiteType: number,
        rootSuiteId: number,
        queryName: string,
        queryText: string,
        selection: IListSelection
    ) =>
        createAction(SuitesActionsTypes.createNewQuerySuite, {
            testSuite,
            planId,
            parentSuiteId,
            parentSuiteTitle,
            suiteType,
            rootSuiteId,
            queryName,
            queryText,
            selection
        }),

    /**
     * openCreateEditSuiteQueryForm: Dispatched from new suite -> query based suite from suite context menu or when edit query is clicked.
     */
    openCreateEditSuiteQueryForm: (
        areaPath: string,
        createSave: boolean,
        queryText: string,
        planId: number,
        parentSuiteId: number,
        parentSuiteTitle: string,
        rootSuiteId: number,
        selection: IListSelection | undefined
    ) =>
        createAction(SuitesActionsTypes.openCreateEditSuiteQueryForm, {
            areaPath,
            createSave,
            queryText,
            planId,
            parentSuiteId,
            parentSuiteTitle,
            rootSuiteId,
            selection
        }),

    /**
     * editQuery: Dispatched when edit query is clicked
     */
    editQueryForQueryBasedSuite: (queryText: string, planId: number, suiteId: number, suiteTitle: string) =>
        createAction(SuitesActionsTypes.editQueryForQueryBasedSuite, { queryText, planId, suiteId, suiteTitle }),

    /**
     * queryEdited: when query is edited
     */
    queryEdited: (queryText: string, suiteId: number) => createAction(SuitesActionsTypes.queryEdited, { queryText, suiteId }),

    /**
     * reloadTestPlanAndSuites: reload suites and plan as well as invoke actions for loading the testCases/testPoints.
     * Initiates reloading of the whole plan, suites as well as testcase/testpoints . Needed when a different plan is selected or when reload is required after clone is completed.
     */
    reloadTestPlanAndSuites: (planId: number) => createAction(SuitesActionsTypes.reloadTestPlanAndSuites, { planId }),

    /**
     * createStaticSuite: Dispatched from new suite -> static suite from suite context menu
     */
    createStaticSuite: (planId: number, parentSuiteId: number, parentSuiteTitle: string, suiteType: number, rootSuiteId: number) =>
        createAction(SuitesActionsTypes.createStaticSuite, { planId, parentSuiteId, parentSuiteTitle, suiteType, rootSuiteId }),

    /**
     * changeTreeItemExpandableState: Dispatched when the tree item is either expanded or collapsed
     */
    changeTreeItemExpandableState: (suiteId: number, expandedState: boolean) =>
        createAction(SuitesActionsTypes.changeTreeItemExpandableState, { suiteId, expandedState }),

    /**
     * editTestSuiteName: Dispatched from rename option in suite context menu
     */
    editTestSuiteName: (suiteId: number, suiteTitle: string) => createAction(SuitesActionsTypes.editTestSuiteName, { suiteId, suiteTitle }),

    /**
     * openDeleteTestSuiteDialog: Dispatched from delete option in suite context menu
     */
    openDeleteTestSuiteDialog: (
        planId: number,
        suiteId: number,
        rootSuiteId: number,
        suiteName: string,
        suiteType: Contracts.TestSuiteType,
        selection: IListSelection,
        dispatch: any
    ) => createAction(SuitesActionsTypes.openDeleteTestSuiteDialog, { planId, suiteId, rootSuiteId, suiteName, suiteType, selection, dispatch }),

    /**
     * deleteTestSuite: Dispatched when confirm button of delete suite dialog is clicked
     */
    deleteTestSuite: (
        planId: number,
        suiteId: number,
        rootSuiteId: number,
        suiteName: string,
        suiteType: Contracts.TestSuiteType,
        selection: IListSelection
    ) => createAction(SuitesActionsTypes.deleteTestSuite, { planId, suiteId, rootSuiteId, suiteName, suiteType, selection }),

    /**
     * saveTestSuiteName: Dispatched when the suite name is saved after rename or static suite creation
     */
    saveTestSuiteName: (planId: number, suiteId: number, oldSuiteTitle: string, newSuiteTitle: string, rootSuiteId: number) =>
        createAction(SuitesActionsTypes.saveTestSuiteName, { planId, suiteId, oldSuiteTitle, newSuiteTitle, rootSuiteId }),

    /**
     * showToastNotifications: Dispatched to display a toast notification whenever some information is to be coveyed
     */
    showToastNotifications: (toastMessage: string, toastDurationMs: number, callToActionText?: string, callToActionTextCallback?: () => void, className?: string) =>
        createAction(SuitesActionsTypes.showToastNotifications, { toastMessage, toastDurationMs, callToActionText, callToActionTextCallback, className }),

    /**
     * getSuitePointCount: Dispatched when the suite point count needs to be updated
     */
    getSuitePointCount: (
        planId: number,
        states: string[],
        configurations: number[],
        testers: string[],
        assignedTo: string[],
        outcome?: Contracts.UserFriendlyTestOutcome
    ) => createAction(SuitesActionsTypes.getSuitePointCount, { planId, states, configurations, testers, assignedTo, outcome }),

    /**
     * getSuiteTestCasesCount: Dispatched when the suite test case count needs to be updated
     */
    getSuiteTestCasesCount: (planId: number, states: string[], assignedTo: string[]) =>
        createAction(SuitesActionsTypes.getSuiteTestCasesCount, { planId, states, assignedTo }),

    /**
     * showSuitePointsCount: Dispatched after fetching the suite point count to update it in UX
     */
    showSuitePointsCount: (planId: number, suitePointCount: Contracts.TestEntityCount[], showTestsFromChildSuitesEnabled?: boolean) =>
        createAction(SuitesActionsTypes.showSuitePointsCount, { planId, suitePointCount, showTestsFromChildSuitesEnabled }),

    /**
     * showSuiteTestCasesCount: Dispatched after fetching the suite test case count to update it in the UX
     */
    showSuiteTestCasesCount: (planId: number, suiteTestCasesCount: Contracts.TestEntityCount[]) =>
        createAction(SuitesActionsTypes.showSuiteTestCasesCount, { planId, suiteTestCasesCount }),

    /**
     * reorderTestSuite: Dispatched to change order of test suite in case of drag drop tree items
     */
    reorderTestSuite: (planId: number, targetParentSuiteId: number, suiteId: number, targetSuiteId: number, dropAtEnd: boolean) =>
        createAction(SuitesActionsTypes.reorderTestSuite, { planId, targetParentSuiteId, suiteId, targetSuiteId, dropAtEnd }),

    /**
     * reorderTestSuiteSucceeded: Dispatched to reorder test suite tree after drag drop operation
     */
    reorderTestSuiteSucceeded: (suiteId: number, targetSuiteId: number, dropAtEnd: boolean) =>
        createAction(SuitesActionsTypes.reorderTestSuiteSucceeded, { suiteId, targetSuiteId, dropAtEnd }),

    /**
     * updateTestSuiteParent: Dispatched to change parent of test suite in case of drag drop tree items
     */
    updateTestSuiteParent: (planId: number, suiteId: number, targetParentSuiteId: number) =>
        createAction(SuitesActionsTypes.updateTestSuiteParent, { planId, suiteId, targetParentSuiteId }),

    /**
     * updateTestSuiteParentSucceeded: Dispatched to update test suite tree after drag drop operation
     */
    updateTestSuiteParentSucceeded: (suiteId: number, targetParentSuiteId: number) =>
        createAction(SuitesActionsTypes.updateTestSuiteParentSucceeded, { suiteId, targetParentSuiteId }),

    /**
     * updateParentAndReorderSuite: Dispatched to change order as well as parent of test suite in case of drag drop tree items
     */
    updateParentAndReorderSuite: (planId: number, targetParentSuiteId: number, suiteId: number, targetSuiteId: number, dropAtEnd: boolean) =>
        createAction(SuitesActionsTypes.updateParentAndReorderSuite, { planId, targetParentSuiteId, suiteId, targetSuiteId, dropAtEnd }),

    // Actions invoked to load test point for a selected suite
    loadTestPointsForSelectedSuite: (testPlanId: number, testSuiteId: number, continuationToken?: string) =>
        createAction(SuitesActionsTypes.loadTestPointsForSelectedSuite, { testPlanId, testSuiteId, continuationToken }),

    /**
     * assignTestersToSuite: Dispatched to assign testers to a suite to run all tests
     */
    assignTestersToSuite: (planId: number, suite: ISuiteTableItem) => createAction(SuitesActionsTypes.assignTestersToSuite, { planId, suite }),

    /**
     * openExportHTMLDialog: Dispatched to launch a dialog to export the different properties of test suites and test plans
     */
    openExportHTMLDialog: (
        planId: number,
        suiteId: number,
        pointCount: number | undefined,
        rootSuiteId: number,
        totalPointCount: number | undefined
    ) => createAction(SuitesActionsTypes.openExportHTMLDialog, { planId, suiteId, pointCount, rootSuiteId, totalPointCount }),

    /**
     * expandCollapseAllSuites: Dispatched to expand or collapse all suites, true->expand all, false->collapse all
     */
    expandCollapseAllSuites: (expandCollapseBool: boolean) => createAction(SuitesActionsTypes.expandCollapseAllSuites, { expandCollapseBool }),

    /**
     * fetchSplitterWidth: Dispatched to fetch splitter width via settings service
     */
    fetchSplitterWidth: () => createAction(SuitesActionsTypes.fetchSplitterWidth, {}),

    /**
     * getSuiteCloneOpState: Get state of suite clone operation.
     */
    getSuiteCloneOpState: (planId: number, opId: number, initialCall?: boolean, project?: string) =>
        createAction(SuitesActionsTypes.getSuiteCloneOpState, { planId, opId, initialCall, project }),

    /**
     * moveTestCaseToSuite: Drag drop test case to different suite.
     */
    moveTestCaseToSuite: (
        testPlanId: number,
        sourceSuiteId: number,
        sourceSuiteRevision: number,
        destinationSuiteId: number,
        destinationSuiteRevision: number,
        testCaseIds: number[]
    ) =>
        createAction(SuitesActionsTypes.moveTestCaseToSuite, {
            testPlanId,
            sourceSuiteId,
            sourceSuiteRevision,
            destinationSuiteId,
            destinationSuiteRevision,
            testCaseIds
        }),

    /**
     * moveTestCaseToSuiteSucceeded: Dispatched to update test cases table after drag dropping test cases to a suite.
     */
    moveTestCaseToSuiteSucceeded: (testCaseIds: number[]) => createAction(SuitesActionsTypes.moveTestCaseToSuiteSucceeded, { testCaseIds }),

    /**
     * saveSplitterWidth: Dispatched to save splitter width via settings service
     */
    saveSplitterWidth: (splitterWidth: number) => createAction(SuitesActionsTypes.saveSplitterWidth, { splitterWidth }),

    /**
     * exportTestCasesToCSV: Dispatched to export test cases in a suite to CSV file
     */
    exportTestCasesToCSV: (planId: number, suiteId: number, planName: string, suiteName: string, testCaseIds?: number[]) =>
        createAction(SuitesActionsTypes.exportTestCasesToCSV, { planId, suiteId, planName, suiteName, testCaseIds }),

    /**
     * exportTestCasesToExcel: Dispatched to export test cases in a suite to Excel file
     */
    exportTestCasesToExcel: (planId: number, suiteId: number, planName: string, suiteName: string, testCaseIds?: number[]) =>
        createAction(SuitesActionsTypes.exportTestCasesToExcel, { planId, suiteId, planName, suiteName, testCaseIds }),

    /**
     * openRequirementWorkItemForm: Dispatched to open requirement workitem form for the selected RBS 
     */
    openRequirementWorkItemForm: (requirementId: number) => createAction(SuitesActionsTypes.openRequirementWorkItemForm, { requirementId }),

    /**
     * filterTestSuites: Dispatched to filter test suites
     */
    filterTestSuites: (keyword: string, callback: any) => createAction(SuitesActionsTypes.filterTestSuites, {keyword, callback}),
};

export const enum SuitesActionsTypes {
    changeSelectedSuite = "Suites/changeSelectedSuite",
    loadInitialTestData = "Suites/loadInitialTestData",
    loadInitialTestDataSucceeded = "Suites/loadInitialTestDataSucceeded",
    getMineAllPageUrl = "Suites/getMineAllPageUrl",
    openTestSuiteWitForm = "Suites/openTestSuiteWitForm",
    updateTestSuiteName = "Suites/updateTestSuiteName",
    loadTestPlan = "Suites/loadTestPlan",
    loadTestSuites = "Suites/loadTestSuites",
    updateLoadingStatus = "Suites/updateLoadingStatus",
    createNewReqSuite = "Suites/createNewReqSuite",
    createNewQuerySuite = "Suites/createNewQuerySuite",
    loadNewSuites = "Suites/loadNewSuites",
    reloadTestPlanAndSuites = "Suites/reloadTestPlanAndSuites",
    createStaticSuite = "Suites/createStaticSuite",
    changeTreeItemExpandableState = "Suites/changeTreeItemExpandableState",
    editTestSuiteName = "Suites/editTestSuiteName",
    openDeleteTestSuiteDialog = "Suites/openDeleteTestSuiteDialog",
    deleteTestSuite = "Suites/deleteTestSuite",
    saveTestSuiteName = "Suites/saveTestSuiteName",
    updateReloadState = "Suites/updateReloadState",
    showToastNotifications = "Suites/showToastNotifications",
    removeSuite = "Suites/removeSuite",
    getSuitePointCount = "Suites/getSuitePointCount",
    getSuiteTestCasesCount = "Suites/getSuiteTestCasesCount",
    showSuitePointsCount = "Suites/showSuitePointsCount",
    showSuiteTestCasesCount = "Suites/showSuiteTestCasesCount",
    openCreateEditSuiteQueryForm = "Suites/openCreateEditSuiteQueryForm",
    editQueryForQueryBasedSuite = "Suites/editQueryForQueryBasedSuite",
    queryEdited = "Suites/queryEdited",
    reorderTestSuite = "Suites/reorderTestSuite",
    reorderTestSuiteSucceeded = "Suites/reorderTestSuiteSucceeded",
    updateTestSuiteParent = "Suites/updateTestSuiteParent",
    updateTestSuiteParentSucceeded = "Suites/updateTestSuiteParentSucceeded",
    updateParentAndReorderSuite = "Suites/updateParentAndReorderSuite",
    loadTestPointsForSelectedSuite = "Suites/loadTestPointsForSelectedSuite",
    assignTestersToSuite = "Suites/assignTestersToSuite",
    openExportHTMLDialog = "Suites/openExportHTMLDialog",
    expandCollapseAllSuites = "Suites/expandCollapseAllSuites",
    saveSplitterWidth = "Suites/saveSplitterWidth",
    fetchSplitterWidth = "Suites/fetchSplitterWidth",
    getSuiteCloneOpState = "Suites/getSuiteCloneOpState",
    moveTestCaseToSuite = "Suites/moveTestCaseToSuite",
    moveTestCaseToSuiteSucceeded = "Suites/moveTestCaseToSuiteSucceeded",
    exportTestCasesToCSV = "Suites/exportTestCasesToCSV",
    exportTestCasesToExcel = "Suites/exportTestCasesToExcel",
    openRequirementWorkItemForm = "Suites/openRequirementWorkItemForm",
    filterTestSuites = "Suites/filterTestSuites"
}

export type SuitesActions = ActionsUnion<typeof SuitesActions>;
